// eslint-disable-next-line import/no-unresolved
import { Swiper } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import Visibility from '@components/visibility';
import { DEFAULT_CSS_CLASS } from '@constants';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Label from '@components/block-label';
import classnames from 'classnames';
import {
  setUniqueIdAttribute,
  sliderMediaBreakPoints,
  cloneChildBlockByParentClientId,
} from '@helpers/utils.js';
import {
  setSliderSettingAttribute as updateResponsiveAttributes,
  removeSliderSettingAttribute as updateSliderSettingAttribute,
} from '@helpers/slider-utils.js';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import SliderSettings from './__partials/sliderSettings';
import { v4 as uuidv4 } from 'uuid';
import { sliderIcon, slidePreviewArrow, slideNextArrow } from './icons';
import { title } from './block.json';

import './editor.scss';

const {
  i18n: { __ },
  blocks: { createBlock },
  components: { Button, PanelBody },
  data: { withSelect },
  element: { useEffect, useState, useRef },
  blockEditor: { useBlockProps, useInnerBlocksProps },
} = wp;

const DEFAULT_NUMBER_OF_SLIDES = 3;
const generateTabSliderTemplates = (numberOfSlides) => {
  const sliderTemplates = [];
  for (let i = 0; i < numberOfSlides; i++) {
    const sliderTemplate = [
      'b3rg/slider-slide',
      {},
      [
        [
          'core/paragraph',
          {
            placeholder: `Slider content ${i + 1} ...`,
          },
        ],
      ],
    ];
    sliderTemplates.push(sliderTemplate);
  }
  return sliderTemplates;
};

const Edit = ({
  name,
  attributes,
  setAttributes,
  innerBlocks,
  hasSelectedInnerBlock,
  clientId,
  selectedBlockClientId,
  selectedSlideClientId,
}) => {
  const {
    slider_settings: sliderSettings,
    blockVisibility,
    blockClassNames,
    label,
    enableFocusableIcon,
  } = attributes;
  const [swiperInst, setSwiperInst] = useState(null);
  const slideNextButton = useRef();
  const slidePrevButton = useRef();
  const { replaceInnerBlocks, selectBlock } =
    wp.data.dispatch('core/block-editor');
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [t, _t] = useState(null);

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.SLIDER;
  const classNames = getBlockClassNames(blockClassNames);
  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${classNames}`]: classNames,
      'has-focusable-icon': enableFocusableIcon,
      disable: !blockVisibility,
    }),
  });

  const { children, ...innerBlockProps } = useInnerBlocksProps(blockProps, {
    allowedBlocks: ['b3rg/slider-slide'],
    templateLock: false,
    template: generateTabSliderTemplates(DEFAULT_NUMBER_OF_SLIDES),
    orientation: 'horizontal',
    __experimentalCaptureToolbars: true,
  });

  useEffect(() => {
    setUniqueIdAttribute(name, 'blockId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  useEffect(() => {
    setAttributes({ identifier: uuidv4() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasSelectedInnerBlock && swiperInst) {
      const selectedSlideIndex = innerBlocks.findIndex(
        (ib) => ib.clientId === selectedSlideClientId,
      );
      if (selectedSlideIndex !== -1) {
        setActiveSlideIndex(selectedSlideIndex);
        _t(Math.random());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockClientId, selectedSlideClientId, hasSelectedInnerBlock]);

  useEffect(() => {
    if (swiperInst && innerBlocks.length !== swiperInst.slides?.length) {
      _t(Math.random());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerBlocks]);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    swiperInst?.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onClickNewSlide = () => {
    const newSlideBlock = createBlock('b3rg/slider-slide');
    const updatedSlidesArr = [...innerBlocks, newSlideBlock];
    replaceInnerBlocks(clientId, updatedSlidesArr);
    setActiveSlideIndex(updatedSlidesArr.length);
    _t(Math.random());
    selectBlock(newSlideBlock.clientId);
  };

  // Update Mobile, Tablet and Table Landscape setting(s) through cloned copies
  const setSliderSettingAttribute = (
    attribute,
    layout,
    property,
    childProperty,
    value,
  ) => {
    updateResponsiveAttributes(
      attributes,
      setAttributes,
      attribute,
      layout,
      property,
      childProperty,
      value,
    );
  };

  // Update Mobile, Tablet and Table Landscape setting(s) through cloned copies
  const removeSliderSettingAttribute = (
    attribute,
    layout,
    attributeKey,
    attributeChildKey,
  ) => {
    updateSliderSettingAttribute(
      attributes,
      setAttributes,
      attribute,
      layout,
      attributeKey,
      attributeChildKey,
    );
  };

  // Swiper settings
  const editViewSliderSettings = {
    direction: 'horizontal',
    effect: 'slide',
    initialSlide: activeSlideIndex,
    loop: false,
    centeredSlides: false,
    observeParents: false,
    observer: false,
    slidesPerGroup: 1,
    slidesPerView: 1,
    spaceBetween: 30,
    speed: 600,
    zoom: false,
    allowTouchMove: false, // draggable - do not turn on for edit view
    modules: [Navigation, Pagination],
    navigation: {
      clickable: true,
      nextEl: slideNextButton.current,
      prevEl: slidePrevButton.current,
    },
    onSwiper: setSwiperInst,
    onAfterInit({ activeIndex }) {
      setActiveSlideIndex(activeIndex);
    },
    onTransitionEnd({ activeIndex }) {
      setActiveSlideIndex(activeIndex);
    },
  };

  children.type.displayName = 'SwiperSlide';

  const onClickSlidePrev = () => {
    swiperInst?.slideTo(activeSlideIndex - 1);
  };

  const onClickSlideNext = () => {
    swiperInst?.slideTo(activeSlideIndex + 1);
  };

  return (
    <>
      <InspectorTabs>
        <InspectorControlsGeneral>
          <Label {...{ label, setAttributes }} />
          <Visibility {...{ blockVisibility, setAttributes }} />
          <PanelBody title={__('Slider Settings', 'b3rg')} initialOpen={true}>
            {sliderMediaBreakPoints
              .map((sliderMediaBreakPoint) => {
                const deviceSlug = sliderMediaBreakPoint.slug;
                return (
                  <SliderSettings
                    key={deviceSlug}
                    {...{
                      sliderMediaBreakPoint,
                      sliderSettings: sliderSettings[deviceSlug],
                      slides: innerBlocks,
                      setSliderSettingAttribute,
                      removeSliderSettingAttribute,
                    }}
                  />
                );
              })
              .reverse()}
          </PanelBody>
        </InspectorControlsGeneral>
        <InspectorControlsAppearance>
          <ClassDropdown
            {...{
              blockClassNames,
              setAttributes,
              blockClassPrefix: defaultBlockCssClass,
            }}
          />
          <FocusableIconSwitcher
            {...{
              enableFocusableIcon,
              setAttributes,
            }}
          />
        </InspectorControlsAppearance>
      </InspectorTabs>
      <div {...innerBlockProps}>
        {enableFocusableIcon && (
          <FocusableIcon
            {...{ title, attributes, setAttributes, icon: sliderIcon }}
          />
        )}
        <Button
          ref={slidePrevButton}
          className="slider-nav nav-prev components-button is-primary"
          onClick={onClickSlidePrev}
        >
          {slidePreviewArrow}
        </Button>
        <Swiper
          {...{
            key: t,
            ...editViewSliderSettings,
          }}
        >
          {children}
        </Swiper>
        <Button
          ref={slideNextButton}
          className="slider-nav nav-next components-button is-primary"
          onClick={onClickSlideNext}
        >
          {slideNextArrow}
        </Button>
        <div className="custom-appender-btn-group">
          <button
            className="custom-appender-btn clone-last-item"
            onClick={() => cloneChildBlockByParentClientId(clientId)}
          >
            {__('Clone Last Slide', 'b3rg')}
          </button>
          <button
            className="custom-appender-btn add-empty-slide"
            onClick={onClickNewSlide}
          >
            {__('Add Empty Slide', 'b3rg')}
          </button>
        </div>
      </div>
    </>
  );
};

export default withSelect((select, { clientId }) => {
  const {
    getBlocks,
    getSelectedBlockClientId,
    hasSelectedInnerBlock: getHasSelectedInnerBlock,
    getClientIdsWithDescendants,
    getBlockParents,
    getBlocksByClientId,
  } = select('core/block-editor');
  const innerBlocks = getBlocks(clientId);
  const selectedBlockClientId = getSelectedBlockClientId();
  const descendantClientIds = getClientIdsWithDescendants(clientId);
  const hasSelectedInnerBlock = getHasSelectedInnerBlock(clientId);
  let selectedSlideClientId = hasSelectedInnerBlock
    ? selectedBlockClientId
    : undefined;
  if (
    !hasSelectedInnerBlock &&
    selectedBlockClientId &&
    descendantClientIds.indexOf(selectedBlockClientId) !== -1
  ) {
    const blockParentClientIds = getBlockParents(selectedBlockClientId);
    selectedSlideClientId = getBlocksByClientId(blockParentClientIds).find(
      ({ name }) => name === 'b3rg/slider-slide',
    )?.clientId;
  }
  return {
    innerBlocks,
    selectedBlockClientId,
    hasSelectedInnerBlock:
      hasSelectedInnerBlock || typeof selectedSlideClientId !== 'undefined',
    selectedSlideClientId,
  };
})(Edit);
